import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/apiClient';
import { Sheet, SheetHeader, SheetTitle, SheetBody, SheetActions, Input, Button } from './ui';
import './ChangePasswordModal.css';

interface ChangePasswordModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

function ChangePasswordModal({ onClose, onSuccess }: ChangePasswordModalProps) {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError(t('changePassword.mismatch'));
      return;
    }

    if (newPassword.length < 6) {
      setError(t('changePassword.tooShort'));
      return;
    }

    setLoading(true);
    try {
      await api.patch('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || t('changePassword.failed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={true} onClose={onClose} className="change-password-sheet">
      <SheetHeader>
        <SheetTitle>{t('changePassword.title')}</SheetTitle>
      </SheetHeader>
      <SheetBody>
        <form onSubmit={handleSubmit} className="change-password-form" id="change-password-form">
          {error && <div className="change-password-error">{error}</div>}
          <Input
            type="password"
            label={t('changePassword.currentPassword')}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            autoComplete="current-password"
            placeholder={t('changePassword.currentPlaceholder')}
          />
          <Input
            type="password"
            label={t('changePassword.newPassword')}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            placeholder={t('changePassword.newPlaceholder')}
          />
          <Input
            type="password"
            label={t('changePassword.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            placeholder={t('changePassword.confirmPlaceholder')}
          />
        </form>
      </SheetBody>
      <SheetActions>
        <Button type="submit" form="change-password-form" variant="primary" disabled={loading} onClick={handleSubmit}>
          {loading ? t('changePassword.changing') : t('changePassword.submit')}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </SheetActions>
    </Sheet>
  );
}

export default ChangePasswordModal;
